import React, { useState } from 'react';

import { Form, Formik } from 'formik';
import DOMPurify from 'isomorphic-dompurify';
import { useToasts } from 'react-toast-notifications';

import Button from '@components/common/Button/Button';
import Checkbox from '@components/common/Formik/FormikCheckBox';
import Input from '@components/common/Formik/FormikInputField';
import InputErrorMessage from '@components/common/Formik/InputErrorMessage/InputErrorMessage';
import HomeHeading from '@components/common/Heading/HomeHeading/HomeHeading';
import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import useWindowSize from '@components/common/hooks/useWindowSize';
import Text from '@components/common/Text/Text';
import ColoredShareIcon from '@components/lpc/Competitions/ColoredShareIcon';
import { S_DEVICE } from '@components/lpc/Constants';
import ShareModal from '@components/lpc/ShareModal/ShareModal';
import { contestType } from '@propTypes/potTypes';
import AlertsService from '@services/domain/Lpc/AlertsService';
import ObjectService from '@services/utils/ObjectService';
import { compitionsGameValidationSchema } from '@validations/userSchema';

import css from './Competitions.module.scss';

export type CompetitionsProps = {
    contest: contestType;
    userAccount: any;
    showAdminBanner: boolean;
};
const Competitions = ({ contest, userAccount, showAdminBanner }: CompetitionsProps) => {
    const { width } = useWindowSize();
    const { addToast } = useToasts();
    const [showConfirmationBlock, setShowConfirmationBlock] = useState<boolean>(false);
    const [requiredPhone, setRequiredPhone] = useState<boolean>(false);
    const [requiredAddress, setRequiredAddress] = useState<boolean>(false);
    const [shareModalVisible, setShareModalVisible] = useState<boolean>(false);
    const { t } = useTranslateLpc('common');

    const getInitialValues = () => {
        return {
            firstName: ObjectService.hasContent(userAccount) ? userAccount.firstName : '',
            lastName: ObjectService.hasContent(userAccount) ? userAccount.lastName : '',
        };
    };

    const onConfirm = (values: any) => {
        const body = {
            contestID: contest.contestId,
            firstName: values.firstName,
            lastName: values.lastName,
            email: values.email,
            telephone: values.phoneNumber,
            address: values.postalAddress,
            acceptCGU: values.first_condition,
            acceptNewsletter: values.second_condition,
            customCheckbox: values.third_condition,
        };
        AlertsService.setCompetitionGame(body)
            .then(() => {
                setShowConfirmationBlock(true);
            })
            .catch(() =>
                addToast(t('competition-game.error'), {
                    appearance: 'error',
                    autoDismiss: true,
                }),
            );
    };

    const renderForm = (isValid: boolean) => {
        return (
            <Form className={css.competitions_container__right_side__form}>
                {width <= S_DEVICE && (
                    <div className={css.competitions_container__left_side}>
                        <img alt={contest.pageTitle} src={process.env.NEXT_PUBLIC_BACKEND_URL + contest.pageImgUrl} />
                    </div>
                )}
                <HomeHeading
                    title={contest.pageTitle}
                    paragraph={contest.pageWording}
                    customTitle={css.competitions_container__right_side__title}
                    customParagraph={css.competitions_container__right_side__html_block}
                    customStyle={{ color: contest.textColor }}
                />
                <div className={css.competitions_container__right_side__inputs_container}>
                    <div className={css.competitions_container__right_side__one_input}>
                        <Input
                            name="firstName"
                            type="text"
                            label={t('competition-game.label-first-name')}
                            isFloatingLabel
                            customCssStyle={{ color: contest.textColor, borderColor: contest.textColor }}
                        />
                    </div>
                    <div className={css.competitions_container__right_side__one_input}>
                        <Input
                            name="lastName"
                            type="text"
                            label={t('competition-game.label-last-name')}
                            isFloatingLabel
                            customCssStyle={{ color: contest.textColor, borderColor: contest.textColor }}
                        />
                    </div>
                </div>
                <div className={css.competitions_container__right_side__one_input}>
                    <Input
                        name="email"
                        type="text"
                        label={t('competition-game.label-email')}
                        isFloatingLabel
                        customCssStyle={{ color: contest.textColor, borderColor: contest.textColor }}
                    />
                </div>

                {contest.phoneShown && (
                    <div className={css.competitions_container__right_side__one_input}>
                        <Input
                            customStyle={requiredPhone && 'required_field'}
                            name="phoneNumber"
                            type="text"
                            label={
                                contest.phoneRequired
                                    ? t('competition-game.label-phone-required')
                                    : t('competition-game.label-phone')
                            }
                            onHandleChange={() => {
                                setRequiredPhone(false);
                            }}
                            isFloatingLabel
                            customCssStyle={{ color: contest.textColor, borderColor: contest.textColor }}
                        />
                        {requiredPhone && <InputErrorMessage name={'phoneNumber'} messageCode={'REQUIRED'} />}
                    </div>
                )}

                {contest.addressShown && (
                    <div className={css.competitions_container__right_side__one_input}>
                        <Input
                            customStyle={requiredAddress && 'required_field'}
                            name="postalAddress"
                            type="text"
                            label={
                                contest.addressRequired
                                    ? t('competition-game.label-postal-adresse-required')
                                    : t('competition-game.label-postal-adresse')
                            }
                            onHandleChange={() => {
                                setRequiredAddress(false);
                            }}
                            isFloatingLabel
                            customCssStyle={{ color: contest.textColor, borderColor: contest.textColor }}
                        />
                        {requiredAddress && <InputErrorMessage name={'postalAddress'} messageCode={'REQUIRED'} />}
                    </div>
                )}

                {contest.condition1 && (
                    <div className={css.competitions_container__right_side__checkbox}>
                        <Checkbox
                            name={'first_condition'}
                            defaultStyle={{ border: '1px solid ' + contest.textColor }}
                            customStyle={{
                                backgroundColor: contest.buttonColor,
                                border: '1px solid ' + contest.textColor,
                            }}
                        >
                            <div
                                className={css.competitions_container__right_side__html_block}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        contest.condition1Required ? contest.condition1 + ' *' : contest.condition1,
                                    ),
                                }}
                                style={{ color: contest.textColor }}
                            />
                        </Checkbox>
                    </div>
                )}

                {contest.condition2 && (
                    <div className={css.competitions_container__right_side__checkbox}>
                        <Checkbox
                            name={'second_condition'}
                            defaultStyle={{ border: '1px solid ' + contest.textColor }}
                            customStyle={{
                                backgroundColor: contest.buttonColor,
                                border: '1px solid ' + contest.textColor,
                            }}
                        >
                            <div
                                className={css.competitions_container__right_side__html_block}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        contest.condition2Required ? contest.condition2 + ' *' : contest.condition2,
                                    ),
                                }}
                                style={{ color: contest.textColor }}
                            />
                        </Checkbox>
                    </div>
                )}
                {contest.condition3 && (
                    <div className={css.competitions_container__right_side__checkbox}>
                        <Checkbox
                            name={'third_condition'}
                            defaultStyle={{ border: '1px solid ' + contest.textColor }}
                            customStyle={{
                                backgroundColor: contest.buttonColor,
                                border: '1px solid ' + contest.textColor,
                            }}
                        >
                            <div
                                className={css.competitions_container__right_side__html_block}
                                dangerouslySetInnerHTML={{
                                    __html: DOMPurify.sanitize(
                                        contest.condition3Required ? contest.condition3 + ' *' : contest.condition3,
                                    ),
                                }}
                                style={{ color: contest.textColor }}
                            />
                        </Checkbox>
                    </div>
                )}

                <div className={css.competitions_container__right_side__button_container}>
                    <div className={css.competitions_container__right_side__button_container__button_left}>
                        <Button
                            type={'button'}
                            variant={'primary'}
                            height={'42px'}
                            width={'35%'}
                            customStyle={{
                                color: contest.buttonColor,
                                border: '2px solid ' + contest.borderColor,
                                margin: 'auto',
                            }}
                            onClick={() => setShareModalVisible(true)}
                        >
                            <div
                                className={
                                    css.competitions_container__right_side__button_container__button_left__share_label
                                }
                            >
                                <Text
                                    tag={'span'}
                                    variant={'mini_label'}
                                    color={'brand-primary'}
                                    customStyle={{
                                        color: contest.buttonColor,
                                    }}
                                >
                                    {t('competition-game.share')}
                                </Text>
                                <ColoredShareIcon color={contest.buttonColor} />
                            </div>
                        </Button>
                    </div>
                    <div className={css.competitions_container__right_side__button_container__button_right}>
                        <Button
                            type={'submit'}
                            variant={'primary'}
                            height={'42px'}
                            width={'35%'}
                            customStyle={{
                                backgroundColor: contest.buttonColor,
                                border: '2px solid ' + contest.borderColor,
                                margin: 'auto',
                                opacity: !isValid ? '0.5' : '1',
                            }}
                            isDisabled={!isValid}
                        >
                            {t('competition-game.inscription')}
                        </Button>
                    </div>
                </div>
            </Form>
        );
    };

    return (
        <>
            {showAdminBanner && (
                <Text
                    variant={'mini_label'}
                    color={'txt-primary'}
                    customClass={css.competitions_container__admin_header}
                >
                    <img
                        className={css.competitions_container__admin_header__icon}
                        src="/front-static/icons/display/alert-circle-outline-primary.svg"
                        alt="alert"
                    />
                    <span
                        dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(
                                t('competition-game.admin-header', { name: userAccount.firstName || '' }),
                            ),
                        }}
                    />
                </Text>
            )}
            <div className={css.competitions_container}>
                {width > S_DEVICE && (
                    <div className={css.competitions_container__left_side}>
                        <img alt={contest.pageTitle} src={process.env.NEXT_PUBLIC_BACKEND_URL + contest.pageImgUrl} />
                    </div>
                )}
                {showConfirmationBlock ? (
                    <div className={css.competitions_container__right_side}>
                        <div className={css.competitions_container__right_side__confirmation}>
                            <Text
                                variant={'label'}
                                color={'txt-primary'}
                                html
                                htmlText={t('competition-game.confirmation.text')}
                                customClass={css.competitions_container__right_side__confirmation__title}
                                customStyle={{ color: contest.textColor }}
                            />
                            <Text
                                variant={'body_00'}
                                color={'txt-primary'}
                                html
                                htmlText={t('competition-game.confirmation.desc')}
                                customClass={css.competitions_container__right_side__confirmation__desc}
                                customStyle={{ color: contest.textColor }}
                            />
                            <Text
                                variant={'body_00'}
                                color={'txt-primary'}
                                html
                                htmlText={t('competition-game.confirmation.hint')}
                                customClass={css.competitions_container__right_side__confirmation__hint}
                                customStyle={{ color: contest.textColor }}
                            />
                            <div className={css.competitions_container__right_side__confirmation__btn}>
                                <Button
                                    type={'button'}
                                    variant={'primary'}
                                    height={'42px'}
                                    width={'35%'}
                                    customStyle={{
                                        backgroundColor: contest.buttonColor,
                                        border: '1px solid ' + contest.borderColor,
                                        margin: 'auto',
                                    }}
                                    onClick={() => setShareModalVisible(true)}
                                >
                                    {t('competition-game.share')}
                                </Button>
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className={css.competitions_container__right_side}>
                        <Formik
                            validationSchema={compitionsGameValidationSchema(
                                contest.addressRequired,
                                contest.phoneRequired,
                                contest.condition1Required,
                                contest.condition2Required,
                                contest.condition3Required,
                            )}
                            initialValues={getInitialValues()}
                            onSubmit={(values) => onConfirm(values)}
                        >
                            {({ isValid }) => renderForm(isValid)}
                        </Formik>
                    </div>
                )}
            </div>
            <ShareModal
                isVisible={shareModalVisible}
                onHide={() => {
                    setShareModalVisible(false);
                }}
            />
        </>
    );
};
export default Competitions;
